import { clearSessionAndRedirect } from '@/lib/sessionManager';
import { API_URL, API_KEY } from '@/config/appConfig';
import type { SpeechProviderId } from './types';

/**
 * Issues a short-lived streaming token for whichever STT provider is asked for.
 * Shared across providers (not per-provider) because the two branches below —
 * the dev-direct proxy call and the prod 401/redirect handling — must stay in
 * lockstep; duplicating this per provider would just be two copies to keep in
 * sync by hand. Uses a raw `fetch`, not `apiClient` — the dev-direct path talks
 * to a different origin (our own Vite dev server's `/__dev/stt-token`, not our
 * backend) with no auth header at all, and `apiClient` is hardwired to
 * `VITE_API_URL` plus a blanket 401/403 interceptor that assumes every request
 * is against our backend. `chatService.sendStreamingMessage` is the established
 * precedent for a raw-fetch service in this codebase.
 *
 * `assemblyai` keeps the bare `/assistant/stt-token` path with no query param —
 * that's the contract already documented for the backend team. Every other
 * provider (currently just `elevenlabs`) is disambiguated with `?provider=`, so
 * the backend can add providers without ever touching the assemblyai route.
 */

export interface SttTokenResponse {
  token: string;
  expiresInSeconds: number;
}

export class SttTokenError extends Error {
  constructor(message: string, public cause?: unknown) {
    super(message);
    this.name = 'SttTokenError';
  }
}

function devProxyUrl(provider: SpeechProviderId): string {
  return provider === 'assemblyai' ? '/__dev/stt-token' : `/__dev/stt-token?provider=${provider}`;
}

function prodUrl(provider: SpeechProviderId): string {
  return provider === 'assemblyai' ? `${API_URL}/assistant/stt-token` : `${API_URL}/assistant/stt-token?provider=${provider}`;
}

export async function getSttToken(accessToken: string, provider: SpeechProviderId): Promise<SttTokenResponse> {
  // The guard must be `import.meta.env.DEV`, not a runtime env var: Vite replaces
  // it with the literal `false` in a production build and Rollup dead-code-
  // eliminates this entire block. There is no API key to strip out here anymore
  // (see vite.config.ts's `sttDevTokenProxy`) — this now hits our own dev server,
  // same-origin — but the block stays DEV-gated regardless, since it's still a
  // codepath that only makes sense while `vite dev` is running.
  if (import.meta.env.DEV) {
    if (import.meta.env.VITE_STT_DEV_DIRECT === 'true') {
      // Neither provider's token endpoint supports browser CORS (verified for
      // AssemblyAI: its OPTIONS preflight returns 405 with no
      // Access-Control-Allow-* headers) — a direct browser fetch is blocked
      // before the request ever leaves, key or no key. `/__dev/stt-token` is
      // same-origin middleware on Vite's own dev server (Node, not a browser)
      // that proxies to the right provider for us.
      let response: Response;
      try {
        response = await fetch(devProxyUrl(provider));
      } catch (err) {
        throw new SttTokenError('Could not reach the local dev STT proxy', err);
      }
      if (!response.ok) {
        let detail = '';
        try {
          detail = ((await response.json()) as { error?: string }).error ?? '';
        } catch { /* ignore */ }
        throw new SttTokenError(`Dev STT proxy request failed (${response.status})${detail ? `: ${detail}` : ''}`);
      }
      const data = (await response.json()) as { token: string; expires_in_seconds: number };
      return { token: data.token, expiresInSeconds: data.expires_in_seconds };
    }
  }

  let response: Response;
  try {
    response = await fetch(prodUrl(provider), {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'API-KEY': API_KEY,
      },
    });
  } catch (err) {
    throw new SttTokenError('Could not reach the backend STT token endpoint', err);
  }

  if (response.status === 401) {
    clearSessionAndRedirect('Session expired or invalid (401 Unauthorized)');
    throw new SttTokenError('Unauthorized');
  }
  if (!response.ok) {
    throw new SttTokenError(`STT token request failed (${response.status})`);
  }
  const data = (await response.json()) as { token: string; expires_in_seconds: number };
  return { token: data.token, expiresInSeconds: data.expires_in_seconds };
}
